import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, Download, Check, AlertCircle, Eye, Globe, FileText, Plus, AlertTriangle } from 'lucide-react';
import { authenticatedFetch } from '../utils/auth';
import { getApiBaseUrl } from '../utils/config';
import { toast } from 'react-hot-toast';

const EnhancedPreviewModal = ({ 
  isOpen, 
  onClose, 
  dashboard 
}) => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [availableSections, setAvailableSections] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [sectionContent, setSectionContent] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingSections, setIsFetchingSections] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentView, setCurrentView] = useState('sections'); // 'sections' or 'preview'
  const [hasData, setHasData] = useState(false);
  const [isCheckingData, setIsCheckingData] = useState(true);

  // Check if data exists when modal opens or language changes
  useEffect(() => {
    if (isOpen && dashboard?.id) {
      checkDataAvailability();
    }
  }, [isOpen, dashboard?.id, selectedLanguage]);

  // Fetch available sections when modal opens or language changes (only if data exists)
  useEffect(() => {
    if (isOpen && dashboard?.id && hasData) {
      fetchAvailableSections();
    }
  }, [isOpen, dashboard?.id, selectedLanguage, hasData]);

  const checkDataAvailability = async () => {
    setIsCheckingData(true);
    try {
      console.log(`[EnhancedPreviewModal] Checking data availability for dashboard ${dashboard.id}, language: ${selectedLanguage}`);
      
      // Check data availability for the currently selected language
      const endpoint = `api/dashboards/${dashboard.id}/sections${selectedLanguage === 'ja' ? '/japanese' : ''}`;
      console.log(`[EnhancedPreviewModal] Using endpoint: ${endpoint}`);
      
      const response = await authenticatedFetch(endpoint, { method: 'GET' });

      let hasDataForLanguage = false;

      if (response.ok) {
        const data = await response.json();
        console.log(`[EnhancedPreviewModal] Response data keys:`, Object.keys(data));
        
        const hasContent = Object.values(data).some(content => 
          content && content !== '{}' && content.trim() !== ''
        );
        hasDataForLanguage = hasContent;
        
        console.log(`[EnhancedPreviewModal] Content check:`, {
          totalSections: Object.keys(data).length,
          hasContent,
          sampleContent: Object.entries(data).slice(0, 2).map(([key, value]) => [key, typeof value === 'string' ? value.substring(0, 50) + '...' : value])
        });
      } else {
        console.log(`[EnhancedPreviewModal] Response not ok:`, response.status, response.statusText);
      }

      console.log(`[EnhancedPreviewModal] Data availability check result for ${selectedLanguage}:`, hasDataForLanguage);
      setHasData(hasDataForLanguage);

    } catch (error) {
      console.error('[EnhancedPreviewModal] Error checking data availability:', error);
      setHasData(false);
    } finally {
      setIsCheckingData(false);
    }
  };

  const fetchAvailableSections = async () => {
    if (!hasData) return;
    
    setIsFetchingSections(true);
    try {
      console.log(`[EnhancedPreviewModal] Fetching sections for dashboard ${dashboard.id}, language: ${selectedLanguage}`);
      
      const response = await authenticatedFetch(
        `api/dashboards/${dashboard.id}/available-sections/${selectedLanguage}`,
        { method: 'GET' }
      );

      console.log(`[EnhancedPreviewModal] Response status: ${response.status}`);

      if (!response.ok) {
        if (response.status === 404) {
          console.log('Available sections API not found, falling back to sections endpoint');
          const fallbackResponse = await authenticatedFetch(
            `api/dashboards/${dashboard.id}/sections${selectedLanguage === 'ja' ? '/japanese' : ''}`,
            { method: 'GET' }
          );
          
          if (!fallbackResponse.ok) {
            throw new Error('Failed to fetch sections');
          }
          
          const fallbackData = await fallbackResponse.json();
          const sectionsWithState = Object.entries(fallbackData).map(([key, content]) => ({
            key,
            title: getSectionTitle(key),
            content: content || '',
            hasContent: content && content !== '{}' && content.trim() !== ''
          }));

          setAvailableSections(sectionsWithState);
          
          const sectionsWithContent = sectionsWithState
            .filter(section => section.hasContent)
            .map(section => section.key);
          setSelectedSections(sectionsWithContent);
          
          // Fetch content for selected sections
          await fetchSectionContent(sectionsWithContent, fallbackData);
          return;
        }
        
        throw new Error('Failed to fetch available sections');
      }

      const responseData = await response.json();
      console.log(`[EnhancedPreviewModal] Response data:`, responseData);
      
      let sectionsWithState;
      
      // Handle the new response format from the backend
      if (responseData.available_sections && Array.isArray(responseData.available_sections)) {
        // New format: { available_sections: [{ key, title, has_content }] }
        sectionsWithState = responseData.available_sections.map(section => ({
          key: section.key,
          title: section.title || getSectionTitle(section.key),
          content: '',
          hasContent: section.has_content
        }));
      } else {
        // Fallback to old format: { key: content }
        sectionsWithState = Object.entries(responseData).map(([key, content]) => ({
          key,
          title: getSectionTitle(key),
          content: content || '',
          hasContent: content && content !== '{}' && content.trim() !== ''
        }));
      }

      setAvailableSections(sectionsWithState);
      
      const sectionsWithContent = sectionsWithState
        .filter(section => section.hasContent)
        .map(section => section.key);
      setSelectedSections(sectionsWithContent);
      
      // Fetch content for selected sections
      await fetchSectionContent(sectionsWithContent, responseData);
      
    } catch (error) {
      console.error('[EnhancedPreviewModal] Error fetching sections:', error);
      toast.error(t('researchDashboard.errorFetchingSections'));
    } finally {
      setIsFetchingSections(false);
    }
  };

  const fetchSectionContent = async (sectionKeys, sectionsData) => {
    const content = {};
    
    for (const key of sectionKeys) {
      if (sectionsData[key]) {
        content[key] = sectionsData[key];
      } else {
        // Try to fetch individual section content
        try {
          const sectionResponse = await authenticatedFetch(
            `api/dashboards/${dashboard.id}/sections/${key}${selectedLanguage === 'ja' ? '/japanese' : ''}`,
            { method: 'GET' }
          );
          
          if (sectionResponse.ok) {
            const sectionData = await sectionResponse.json();
            content[key] = sectionData.content || '';
          }
        } catch (error) {
          console.error(`Error fetching section ${key}:`, error);
          content[key] = '';
        }
      }
    }
    
    setSectionContent(content);
  };

  const getSectionTitle = (sectionKey) => {
    const translationKeyMapping = {
      'basic_information': 'basic',
      'business_structure': 'business_structure',
      'vision_analysis': 'vision',
      'management_strategy': 'management',
      'management_message': 'management_message',
      'crisis_management': 'crisis_management',
      'dx_analysis': 'dx',
      'financial_analysis': 'financial',
      'competitive_landscape': 'competitive',
      'regulatory_environment': 'regulatory',
      'salesforce_analysis': 'salesforce',
      'latest_news': 'news',
      'account_research': 'account_strategy'
    };

    const translationKey = translationKeyMapping[sectionKey] || sectionKey;
    return t(`researchDashboard.sections.${translationKey}`) || sectionKey;
  };

  const handleSectionToggle = (sectionKey) => {
    setSelectedSections(prev => {
      if (prev.includes(sectionKey)) {
        return prev.filter(key => key !== sectionKey);
      } else {
        return [...prev, sectionKey];
      }
    });
  };

  const handleSelectAll = () => {
    const sectionsWithContent = availableSections
      .filter(section => section.hasContent)
      .map(section => section.key);
    setSelectedSections(sectionsWithContent);
  };

  const handleSelectNone = () => {
    setSelectedSections([]);
  };

  const handlePreview = async () => {
    if (selectedSections.length === 0) {
      toast.error(t('researchDashboard.selectAtLeastOneSection'));
      return;
    }

    setIsGeneratingPdf(true);
    try {
      const company_name = localStorage.getItem(`dashboard_${dashboard.id}_selectedCompany`) || dashboard.title;
      const pdfEndpoint = `api/dashboards/${dashboard.id}/generate-pdf/${selectedLanguage}`;
      
      const response = await authenticatedFetch(
        pdfEndpoint,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/pdf',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            company_name: company_name,
            selected_sections: selectedSections,
            platform_language: selectedLanguage
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate preview');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
      setCurrentView('preview');
      
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error(t('researchDashboard.aiMessages.pdfError'));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleDownload = async () => {
    if (!previewUrl) return;
    
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `${dashboard.title}_report.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    toast.success(t('researchDashboard.pdfSuccess'));
  };

  const navigate = useNavigate();
  
  const handleCreateNewReport = () => {
    // Close the modal and navigate to the dashboard to create a new report
    handleClose();
    // Use React Router navigate instead of window.location for security
    navigate(`/dashboard/${encodeURIComponent(dashboard.id)}`);
  };

  const handleClose = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setCurrentView('sections');
    setHasData(false);
    setIsCheckingData(true);
    setSelectedLanguage('en'); // Reset to default language
    setAvailableSections([]);
    setSelectedSections([]);
    setSectionContent({});
    onClose();
  };

  const hasContentCount = availableSections.filter(section => section.hasContent).length;
  const selectedCount = selectedSections.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {dashboard?.title} - {t('common.preview')}
            </h2>
            
            {/* Language Selector - Always show, but data availability will be checked per language */}
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedLanguage('en')}
                className={`px-3 py-1 rounded flex items-center ${
                  selectedLanguage === 'en'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Globe size={14} className="mr-1" />
                English
              </button>
              <button
                onClick={() => setSelectedLanguage('ja')}
                className={`px-3 py-1 rounded flex items-center ${
                  selectedLanguage === 'ja'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Globe size={14} className="mr-1" />
                日本語
              </button>
            </div>
          </div>
          
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {isCheckingData ? (
            // Loading state while checking data
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-600">{t('common.loading')}</p>
              </div>
            </div>
          ) : !hasData ? (
            // No data available - show create new report message
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md mx-auto p-8">
                <div className="bg-yellow-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <AlertTriangle className="text-yellow-600" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {t('researchDashboard.noReportCreated') || 'No Report Created Yet'}
                </h3>
                <p className="text-gray-600 mb-6">
                  {t('researchDashboard.noReportDescription') || 'This dashboard doesn\'t have any research data yet. Create a new report to get started.'}
                </p>
                <button
                  onClick={handleCreateNewReport}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
                >
                  <Plus size={20} className="mr-2" />
                  {t('researchDashboard.createNewReport') || 'Create New Report'}
                </button>
              </div>
            </div>
          ) : currentView === 'sections' ? (
            <div className="h-full flex flex-col">
              {/* Section Selection */}
              <div className="flex-1 overflow-y-auto p-6">
                {isFetchingSections ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">{t('researchDashboard.loadingSections')}</span>
                  </div>
                ) : (
                  <div>
                    {/* Section Selection Header */}
                    <div className="bg-blue-50 rounded-lg p-4 mb-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-blue-900">
                            {hasContentCount} {t('researchDashboard.sectionsAvailable')} • {selectedCount} {t('researchDashboard.sectionsSelected')}
                          </span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={handleSelectAll}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {t('researchDashboard.selectAll')}
                          </button>
                          <button
                            onClick={handleSelectNone}
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {t('researchDashboard.selectNone')}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Warning if no sections selected */}
                    {selectedCount === 0 && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center">
                          <AlertCircle className="text-yellow-600 mr-2" size={20} />
                          <span className="text-yellow-800 font-medium">
                            {t('researchDashboard.selectAtLeastOneSection')}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Sections List */}
                    <div className="space-y-3">
                      {availableSections
                        .filter(section => section.hasContent)
                        .map(section => (
                          <div
                            key={section.key}
                            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                              selectedSections.includes(section.key)
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => handleSectionToggle(section.key)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  selectedSections.includes(section.key)
                                    ? 'border-blue-500 bg-blue-500'
                                    : 'border-gray-300'
                                }`}>
                                  {selectedSections.includes(section.key) && (
                                    <Check size={12} className="text-white" />
                                  )}
                                </div>
                                <span className="font-medium text-gray-900">
                                  {section.title}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500">
                                  {section.content.length > 0 ? `${Math.ceil(section.content.length / 100)} words` : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>

                    {availableSections.filter(section => section.hasContent).length === 0 && (
                      <div className="text-center py-8">
                        <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-800 mb-2">
                          {t('researchDashboard.noSectionsAvailable')}
                        </h3>
                        <p className="text-gray-500">
                          {t('researchDashboard.noSectionsDescription')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isGeneratingPdf}
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handlePreview}
                  disabled={selectedSections.length === 0 || isGeneratingPdf}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    selectedSections.length === 0 || isGeneratingPdf
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {isGeneratingPdf ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('researchDashboard.generatingPdf')}
                    </>
                  ) : (
                    <>
                      <Eye size={16} className="mr-2" />
                      {t('common.preview')}
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            /* Preview View */
            <div className="h-full flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setCurrentView('sections')}
                    className="text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <FileText size={16} className="mr-2" />
                    {t('common.backToSections')}
                  </button>
                  <span className="text-sm text-gray-600">
                    {selectedCount} {t('researchDashboard.sectionsSelected')} • {selectedLanguage === 'ja' ? '日本語' : 'English'}
                  </span>
                </div>
                <button
                  onClick={handleDownload}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download size={16} className="mr-2" />
                  {t('researchDashboard.downloadPdf')}
                </button>
              </div>
              
              <div className="flex-1 p-4 overflow-hidden">
                {previewUrl ? (
                  <iframe
                    src={previewUrl}
                    className="w-full h-full border-0"
                    title="PDF Preview"
                  />
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                      <p className="text-gray-600">{t('researchDashboard.generatingPdf')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedPreviewModal; 